import { fitsInlinePreview } from '@sd/client-core';
import { inlinePlayback, isInlinePreviewable, type FileInfo } from '@sd/contracts';
import { useMemo } from 'react';
import { formatBytes } from '../../format';
import { Icon } from '../../icons/Icon';
import { Button, IconButton } from '../../ui/controls';
import { dismissDialog, Modal } from '../../ui/overlays';
import { FileIcon, isPreviewTile, MediaPlayer, ScanTag, useDownload } from '../attachments';
import { useAppEnv, useClient, useClientState } from '../context';
import { ImageViewer } from './ImageViewer';

/** Pictures one can swipe to from `file`: `gallery`, else those of its conversation loaded here, oldest first. */
function useGallery(file: FileInfo, gallery: FileInfo[] | undefined): FileInfo[] {
  const messages = useClientState((s) => s.timelines[file.conversationId]?.messages);
  return useMemo(() => {
    const list = (gallery ?? (messages ?? []).flatMap((m) => (m.deletedAt ? [] : m.attachments))).filter((f) => isPreviewTile(f) && f.scan !== 'infected');
    return list.some((f) => f.id === file.id) ? list : [file];
  }, [file, gallery, messages]);
}

/** The photo viewer for pictures, a player for video and audio; other files show their details (no in-app document rendering). */
export function PreviewModal({ file, gallery }: { file: FileInfo; gallery?: FileInfo[] }) {
  const env = useAppEnv();
  const download = useDownload();
  // A large original is never decoded in the app: it would cost as much memory as its pixels.
  const tooLarge = isInlinePreviewable(file.name) && !fitsInlinePreview(file);
  const image = isInlinePreviewable(file.name) && fitsInlinePreview(file);
  const pictures = useGallery(file, gallery);
  const client = useClient();
  const playable = file.scan === 'clean' && inlinePlayback(file.name) !== null && client.mediaUrl(file.id) !== null;
  if (image) return <ImageViewer files={pictures} startId={file.id} onClose={env.closeModal} />;
  return (
    <Modal open onClose={env.closeModal} labelledBy="mpv-t" id="m-preview" className="lightbox">
      <div className="dlg-head">
        <h2 id="mpv-t" className="ell" style={{ color: '#fff' }}>
          {file.name}
        </h2>
        <IconButton icon="x" label="닫기" style={{ color: '#fff' }} onClick={() => dismissDialog(env.closeModal)} />
      </div>
      <div className="dlg-body">
        {playable ? (
          <MediaPlayer file={file} large />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 4px' }}>
            <FileIcon ext={file.ext} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }} className="ell">
                {file.name}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.75)', marginTop: 4 }}>
                {formatBytes(file.size)} · <ScanTag file={file} />
              </div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.75)', margin: '8px 0 0' }}>
                {tooLarge ? '큰 이미지는 앱 안에서 미리 보지 않아요. 원본을 내려받아 확인하세요.' : '이 형식은 앱 안에서 미리 볼 수 없어요. 원본을 내려받아 확인하세요.'}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="dlg-foot" style={{ borderColor: 'rgba(255,255,255,.12)' }}>
        <span className="grow" style={{ color: 'rgba(255,255,255,.7)' }}>
          <Icon name="checkc" size="xs" /> {file.scan === 'clean' ? '검사 완료 · ' : ''}권한이 확인된 경우에만 열려요
        </span>
        <Button
          variant="primary"
          icon="download"
          disabled={file.scan === 'infected'}
          onClick={() => {
            env.closeModal();
            void download(file);
          }}
        >
          원본 다운로드
        </Button>
      </div>
    </Modal>
  );
}
